import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { HelmetProvider } from 'react-helmet-async';
import GoogleTagManagerSetup from "@/components/GoogleTagManagerSetup";
import CookieConsent from "@/components/CookieConsent";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import UTMCapture from "@/components/UTMCapture";
import LanguageRouteWrapper from "@/components/LanguageRouteWrapper";
import LegacyLanguageRedirect from "@/components/LegacyLanguageRedirect";

// Critical pages - load immediately
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Lazy load non-critical pages for faster initial load
const TreatmentDetail = lazy(() => import("./pages/TreatmentDetail"));
const Treatments = lazy(() => import("./pages/Treatments"));
const Pricing = lazy(() => import("./pages/Pricing"));
const TreatmentComparison = lazy(() => import("./pages/TreatmentComparison"));
const BookingsCalendar = lazy(() => import("./pages/BookingsCalendar"));
const Auth = lazy(() => import("./pages/Auth"));
const Gallery = lazy(() => import("./pages/Gallery"));
const AdminGallery = lazy(() => import("./pages/AdminGallery"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const AdminKnowledge = lazy(() => import("./pages/AdminKnowledge"));
const AdminAgents = lazy(() => import("./pages/AdminAgents"));
const AdminAssistant = lazy(() => import("./pages/AdminAssistant"));
const AdminMetaPixel = lazy(() => import("./pages/AdminMetaPixel"));
const AdminApprovals = lazy(() => import("./pages/AdminApprovals"));
const AdminCMS = lazy(() => import("./pages/AdminCMS"));
const Page = lazy(() => import("./pages/Page"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const AdminBlog = lazy(() => import("./pages/AdminBlog"));
const AdminBlogWriter = lazy(() => import("./pages/AdminBlogWriter"));
const AdminBlogTranslations = lazy(() => import("./pages/AdminBlogTranslations"));

const AdminCompetitorAnalysis = lazy(() => import("./pages/AdminCompetitorAnalysis"));
const AdminWhatsAppAutomation = lazy(() => import("./pages/AdminWhatsAppAutomation"));
const AdminBacklinks = lazy(() => import("./pages/AdminBacklinks"));
const PatientDashboard = lazy(() => import("./pages/PatientDashboard"));
const HairAnalysis = lazy(() => import("./pages/HairAnalysis"));
const BookConsultation = lazy(() => import("./pages/BookConsultation"));
const Search = lazy(() => import("./pages/Search"));
const AdminLiveSupport = lazy(() => import("./pages/AdminLiveSupport"));
const AdminLeads = lazy(() => import("./pages/AdminLeads"));
const AdminBookings = lazy(() => import("./pages/AdminBookings"));

const AdminWhatsApp = lazy(() => import("./pages/AdminWhatsApp"));
const GDPRPolicy = lazy(() => import("./pages/GDPRPolicy"));
const AdminGTMSetup = lazy(() => import("./pages/AdminGTMSetup"));
const AdminEmailTemplates = lazy(() => import("./pages/AdminEmailTemplates"));
const AdminEmailCampaigns = lazy(() => import("./pages/AdminEmailCampaigns"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const AdminSearchConsole = lazy(() => import("./pages/AdminSearchConsole"));
const GoogleAdsLanding = lazy(() => import("./pages/GoogleAdsLanding"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const DataDeletion = lazy(() => import("./pages/DataDeletion"));
const AdminClaudeTest = lazy(() => import("./pages/AdminClaudeTest"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const AdminLandingPages = lazy(() => import("./pages/AdminLandingPages"));
const AdminUptime = lazy(() => import("./pages/AdminUptime"));
const Guides = lazy(() => import("./pages/Guides"));
const GuideDetail = lazy(() => import("./pages/GuideDetail"));

// Page loading skeleton
const PageSkeleton = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse space-y-4">
      <div className="h-8 w-48 bg-muted rounded"></div>
      <div className="h-4 w-32 bg-muted rounded mx-auto"></div>
    </div>
  </div>
);

// Wrap lazy components with Suspense
const LazyRoute = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (was cacheTime)
      refetchOnWindowFocus: false,
    },
  },
});

// Public routes configuration
const publicRoutes = [
  { path: "", element: <Index />, lazy: false },
  { path: "treatments", element: <LazyRoute><Treatments /></LazyRoute> },
  { path: "treatments/:slug", element: <LazyRoute><TreatmentDetail /></LazyRoute> },
  { path: "prices", element: <LazyRoute><Pricing /></LazyRoute> },
  { path: "compare", element: <LazyRoute><TreatmentComparison /></LazyRoute> },
  { path: "guides", element: <LazyRoute><Guides /></LazyRoute> },
  { path: "guides/:slug", element: <LazyRoute><GuideDetail /></LazyRoute> },
  { path: "gallery", element: <LazyRoute><Gallery /></LazyRoute> },
  { path: "blog", element: <LazyRoute><Blog /></LazyRoute> },
  { path: "blog/:slug", element: <LazyRoute><BlogPost /></LazyRoute> },
  { path: "page/:slug", element: <LazyRoute><Page /></LazyRoute> },
  { path: "auth", element: <LazyRoute><Auth /></LazyRoute> },
  { path: "hair-analysis", element: <LazyRoute><HairAnalysis /></LazyRoute> },
  { path: "book-consultation", element: <LazyRoute><BookConsultation /></LazyRoute> },
  { path: "search", element: <LazyRoute><Search /></LazyRoute> },
  { path: "gdpr-policy", element: <LazyRoute><GDPRPolicy /></LazyRoute> },
  { path: "privacy-policy", element: <LazyRoute><PrivacyPolicy /></LazyRoute> },
  { path: "cookie-policy", element: <LazyRoute><GDPRPolicy /></LazyRoute> },
  { path: "terms-of-service", element: <LazyRoute><TermsOfService /></LazyRoute> },
  { path: "data-deletion", element: <LazyRoute><DataDeletion /></LazyRoute> },
  { path: "lp/hair-transplant", element: <LazyRoute><GoogleAdsLanding /></LazyRoute> },
  { path: "lp/:slug", element: <LazyRoute><LandingPage /></LazyRoute> },
  { path: "thank-you", element: <LazyRoute><ThankYou /></LazyRoute> },
];

// Legacy redirects
const legacyRedirects = [
  { from: "hair-transplant", to: "treatments/fue-hair-transplant" },
  { from: "stemcell-therapy", to: "treatments/stem-cell-therapy" },
  { from: "prp", to: "treatments/prp-hair-treatment" },
];


const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <LanguageProvider>
              <GoogleTagManagerSetup>
                <Routes>
                  {/* OAuth consent — MUST match /.lovable/oauth/consent exactly. */}
                  <Route
                    path="/.lovable/oauth/consent"
                    element={<LazyRoute><OAuthConsent /></LazyRoute>}
                  />

                  {/* Default language routes (English - no prefix) */}
                  {publicRoutes.map((route) => (
                    <Route
                      key={route.path || 'index'}
                      path={route.path === "" ? "/" : `/${route.path}`}
                      element={route.element}
                    />
                  ))}

                  {/* Legacy redirects for default language */}
                  {legacyRedirects.map((redirect) => (
                    <Route
                      key={redirect.from}
                      path={`/${redirect.from}`}
                      element={<Navigate to={`/${redirect.to}`} replace />}
                    />
                  ))}

                  {/* Protected User Routes (no language prefix) */}
                  <Route
                    path="/patient-dashboard"
                    element={
                      <ProtectedRoute>
                        <LazyRoute>
                          <PatientDashboard />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />

                  {/* Protected Admin Routes (no language prefix) */}
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminPanel />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/knowledge"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminKnowledge />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/agents"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminAgents />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/meta-pixel"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminMetaPixel />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/assistant"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminAssistant />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/assistant/:threadId"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminAssistant />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/approvals"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminApprovals />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin-gallery"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminGallery />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/blog"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminBlog />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/blog-translations"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminBlogTranslations />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/cms"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminCMS />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/gtm-setup"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminGTMSetup />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/live-support"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminLiveSupport />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/leads"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminLeads />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/bookings"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminBookings />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/admin/whatsapp"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminWhatsApp />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/email-templates"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminEmailTemplates />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/email-campaigns"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminEmailCampaigns />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/search-console"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminSearchConsole />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/blog-writer"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminBlogWriter />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/competitor-analysis"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminCompetitorAnalysis />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/whatsapp-automation"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminWhatsAppAutomation />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/backlinks"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminBacklinks />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/landing-pages"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminLandingPages />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/uptime"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminUptime />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/users"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminUsers />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/claude-test"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <AdminClaudeTest />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/bookings-calendar"
                    element={
                      <ProtectedRoute requireAdmin>
                        <LazyRoute>
                          <BookingsCalendar />
                        </LazyRoute>
                      </ProtectedRoute>
                    }
                  />

                  {/* Language-prefixed routes */}
                  <Route path="/:lang" element={<LanguageRouteWrapper />}>
                    {publicRoutes.map((route) => (
                      <Route
                        key={`lang-${route.path || 'index'}`}
                        path={route.path}
                        element={route.element}
                      />
                    ))}

                    {/* Legacy redirects for language routes */}
                    {legacyRedirects.map((redirect) => (
                      <Route
                        key={`lang-redirect-${redirect.from}`}
                        path={redirect.from}
                        element={<Navigate to={`/${redirect.to}`} replace />}
                      />
                    ))}

                    {/* Protected routes (with language prefix) */}
                    <Route
                      path="patient-dashboard"
                      element={
                        <ProtectedRoute>
                          <LazyRoute>
                            <PatientDashboard />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="admin"
                      element={
                        <ProtectedRoute requireAdmin>
                          <LazyRoute>
                            <AdminPanel />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/knowledge"
                      element={
                        <ProtectedRoute requireAdmin>
                          <LazyRoute>
                            <AdminKnowledge />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/agents"
                      element={
                        <ProtectedRoute requireAdmin>
                          <LazyRoute>
                            <AdminAgents />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/meta-pixel"
                      element={
                        <ProtectedRoute requireAdmin>
                          <LazyRoute>
                            <AdminMetaPixel />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/assistant"
                      element={
                        <ProtectedRoute requireAdmin>
                          <LazyRoute>
                            <AdminAssistant />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/assistant/:threadId"
                      element={
                        <ProtectedRoute requireAdmin>
                          <LazyRoute>
                            <AdminAssistant />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/approvals"
                      element={
                        <ProtectedRoute requireAdmin>
                          <LazyRoute>
                            <AdminApprovals />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin-gallery"
                      element={
                        <ProtectedRoute requireAdmin>
                          <LazyRoute>
                            <AdminGallery />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/blog"
                      element={
                        <ProtectedRoute requireAdmin>
                          <LazyRoute>
                            <AdminBlog />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/blog-translations"
                      element={
                        <ProtectedRoute requireAdmin>
                          <LazyRoute>
                            <AdminBlogTranslations />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/cms"
                      element={
                        <ProtectedRoute requireAdmin>
                          <LazyRoute>
                            <AdminCMS />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/gtm-setup"
                      element={
                        <ProtectedRoute requireAdmin>
                          <LazyRoute>
                            <AdminGTMSetup />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/live-support"
                      element={
                        <ProtectedRoute requireAdmin>
                          <LazyRoute>
                            <AdminLiveSupport />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/leads"
                      element={
                        <ProtectedRoute requireAdmin>
                          <LazyRoute>
                            <AdminLeads />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/bookings"
                      element={
                        <ProtectedRoute requireAdmin>
                          <LazyRoute>
                            <AdminBookings />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="admin/whatsapp"
                      element={
                        <ProtectedRoute requireAdmin>
                          <LazyRoute>
                            <AdminWhatsApp />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/email-templates"
                      element={
                        <ProtectedRoute requireAdmin>
                          <LazyRoute>
                            <AdminEmailTemplates />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/email-campaigns"
                      element={
                        <ProtectedRoute requireAdmin>
                          <LazyRoute>
                            <AdminEmailCampaigns />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/search-console"
                      element={
                        <ProtectedRoute requireAdmin>
                          <LazyRoute>
                            <AdminSearchConsole />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/blog-writer"
                      element={
                        <ProtectedRoute requireAdmin>
                          <LazyRoute>
                            <AdminBlogWriter />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/competitor-analysis"
                      element={
                        <ProtectedRoute requireAdmin>
                          <LazyRoute>
                            <AdminCompetitorAnalysis />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/whatsapp-automation"
                      element={
                        <ProtectedRoute requireAdmin>
                          <LazyRoute>
                            <AdminWhatsAppAutomation />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/backlinks"
                      element={
                        <ProtectedRoute requireAdmin>
                          <LazyRoute>
                            <AdminBacklinks />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="admin/users"
                      element={
                        <ProtectedRoute requireAdmin>
                          <LazyRoute>
                            <AdminUsers />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="bookings-calendar"
                      element={
                        <ProtectedRoute requireAdmin>
                          <LazyRoute>
                            <BookingsCalendar />
                          </LazyRoute>
                        </ProtectedRoute>
                      }
                    />
                  </Route>

                  {/* 404 */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
                <LegacyLanguageRedirect />
                <CookieConsent />
                <UTMCapture />
              </GoogleTagManagerSetup>
            </LanguageProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
